import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts'

interface GapAnalysisData {
    domain: string
    score: number
    [key: string]: any
}

interface BenchmarkConfig {
    key: string
    name: string
    color: string
}

interface GapAnalysisProps {
    data: GapAnalysisData[]
    title?: string
    description?: string
    benchmarks?: BenchmarkConfig[]
}

export function GapAnalysisChart({
    data,
    title = "Gap Analysis",
    description,
    benchmarks = [],
}: GapAnalysisProps) {
    // Build gap data: positive = exceeds max target, negative = below max target
    const gapData = data
        .map(d => {
            // Find the maximum benchmark target for this domain across all selected targets
            let maxTarget = -1
            benchmarks.forEach(bm => {
                if (d[bm.key] != null && d[bm.key] > maxTarget) {
                    maxTarget = d[bm.key]
                }
            })

            if (maxTarget === -1) return null

            return {
                domain: d.domain,
                gap: +(d.score - maxTarget).toFixed(2),
                score: d.score,
                benchmark: maxTarget,
            }
        })
        .filter(d => d !== null)
        .sort((a, b) => a!.gap - b!.gap) // worst gaps first

    if (gapData.length === 0) {
        return (
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    {description && (
                        <p className="text-sm text-gray-600 mt-1">{description}</p>
                    )}
                </div>
                <div className="h-80 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <p>No benchmark data available</p>
                        <p className="text-sm mt-1">Select comparison targets to see gap analysis</p>
                    </div>
                </div>
            </div>
        )
    }

    const maxGap = Math.max(...gapData.map(d => Math.abs(d!.gap)), 1)
    const domainBound = Math.ceil(maxGap)

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {description && (
                    <p className="text-sm text-gray-600 mt-1">{description}</p>
                )}
            </div>

            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={gapData}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis
                            type="number"
                            domain={[-domainBound, domainBound]}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(v: number) => (v > 0 ? `+${v}` : `${v}`)}
                        />
                        <YAxis
                            type="category"
                            dataKey="domain"
                            tick={{ fontSize: 11 }}
                            width={110}
                        />
                        <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={2} />
                        <Tooltip
                            formatter={(value: number) => [
                                `${value > 0 ? '+' : ''}${value.toFixed(1)}`,
                                'Gap to Target',
                            ]}
                            labelFormatter={(label: string) => `Domain: ${label}`}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null
                                const d = payload[0].payload
                                return (
                                    <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                                        <div className="font-semibold text-gray-900 mb-1">{label}</div>
                                        <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">Your Level:</span>
                                            <span className="font-medium">L{d.score.toFixed(0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">Highest Target:</span>
                                            <span className="font-medium">L{d.benchmark.toFixed(0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 border-t mt-1 pt-1">
                                            <span className="text-gray-500">Gap:</span>
                                            <span className={`font-semibold ${d.gap >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                {d.gap > 0 ? '+' : ''}{d.gap.toFixed(0)} level(s)
                                            </span>
                                        </div>
                                    </div>
                                )
                            }}
                        />
                        <Bar dataKey="gap" radius={[4, 4, 4, 4]}>
                            {gapData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.gap >= 0 ? '#22c55e' : '#ef4444'}
                                    fillOpacity={0.8}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-red-500 opacity-80" />
                    Below target
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-green-500 opacity-80" />
                    Meets / exceeds target
                </div>
            </div>
        </div>
    )
}
